import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import classNames from 'classnames';
import { isEqual } from 'lodash';

import Icon from '~/app/common/Icon';

import useTranslation from '~/app/hooks/useTranslation';

import useField from '../useField';
import Field, { FieldProps } from '../Field';

import Preview from './Preview';
import DropDown, { DropDownProps } from './DropDown';

import classes from './style.module.scss';
import FileTransfer from "~/sqlite/services/fileTransfer";

import { useImageEditor } from '~/app/modules/imageEditor/ImageEditorProvider';
import { useStoreState } from '~/store';
import { normalizePickedFile, resolvePickedFileName } from './normalizePickedFile';
import { PickerMode, resolvePickerAccept } from './pickerAccept';

export type FilePayload = {
  file: File
  previewUrl: string
}

export type FilePropsDefault = React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement> & {
  ref?: React.Ref<HTMLInputElement>
  inputClassName?: string
  maxFiles?: number
  preview?: boolean
  showInput?: boolean 
  imageEdit?: boolean
  onlyImage?: boolean
}

export type FileProps = FilePropsDefault & FieldProps

export type FileComponent = React.FC<FileProps>

const FileInput: FileComponent = forwardRef<any, FileProps>((props, ref) => {

  const inputFileRef = useRef<HTMLInputElement>(null);
  const filesRef = useRef<FilePayload[]>([]);

  const [isOpenDropDown, setIsOpenDropDown] = useState<boolean>(false);
  const [files, setFiles] = useState<FilePayload[]>([]);

  const { t } = useTranslation('system.form.file');

  const { openEditor } = useImageEditor();
  const netWorkStatus = useStoreState(state => state.app.offline.status);

  const {
    fieldProps,
    inputProps: { placeholder, onChange, maxFiles, defaultValue, preview, showInput = true, imageEdit = true, onlyImage = true, inputClassName, accept, ...othersInputProps },
  } = useField<FilePropsDefault>(props);

  const toggleStatusBar = useCallback((): void => {
    if (!window['StatusBar']) {
      return;
    }

    window['StatusBar'].hide();
    window['StatusBar'].show();
  }, []);

  const updateFiles = useCallback((nextFiles: FilePayload[]): void => {
    filesRef.current = nextFiles;
    setFiles(nextFiles);

    if (onChange) {
      // @ts-ignore
      onChange({ target: { name: props.name, value: nextFiles.map(({ file }) => file) } });
    }
  }, [onChange, props.name]);

  const remainingFileSlots = useCallback((): number => {
    if (!maxFiles) {
      return Number.MAX_SAFE_INTEGER;
    }
    return Math.max(0, maxFiles - filesRef.current.length);
  }, [maxFiles]);

  const hasFreeFileSlot = useCallback((): boolean => {
    if (remainingFileSlots() > 0) {
      return true;
    }
    toast.error(t('exceedMaxFiles', { max: maxFiles }));
    return false;
  }, [remainingFileSlots, maxFiles, t]);

  const pictureSuccessHandler = useCallback((images): void => {
    if (!window['resolveLocalFileSystemURL']) return;

    if (maxFiles && filesRef.current.length >= maxFiles) {
      toast.error(t('exceedMaxFiles', { max: maxFiles }));
      return;
    }

    toggleStatusBar();

    window['resolveLocalFileSystemURL'](images, fileEntry => {
      (fileEntry as FileEntry).file(file => {
        const reader = new FileReader();

        reader.onload = () => {
          if (reader.result) {
            const fileObj: any = new Blob([reader.result], { type: file.type });
            fileObj.name = file.name;
            fileObj.lastModified = file.lastModified;

            updateFiles([...filesRef.current, {
              previewUrl: URL.createObjectURL(fileObj),
              file: fileObj as File,
            }]);
          }
        };

        reader.readAsArrayBuffer(file);
      });
    });
  }, [maxFiles, t, updateFiles, toggleStatusBar]);

  const pictureErrorHandler = useCallback((): void => {
    toggleStatusBar();
  }, [toggleStatusBar]);

  const removeFile = useCallback((index) => {
    const next = [...filesRef.current];
    next.splice(index, 1);
    if (inputFileRef.current) {
      inputFileRef.current.value = '';
    }
    updateFiles(next);
  }, [updateFiles]);

  const handleImageEditSave = useCallback((editedImage: File | null, index: number = -1, source: string|null = null): void => {
    if(!editedImage) return;

    if (window['cordova'] && source === "CAMERA") {
      const fileTransfer = new FileTransfer();
      fileTransfer.saveFileFromBlob(editedImage, '', true).then((savedImagePath) => {
        pictureSuccessHandler(savedImagePath);
      }).catch((error) => {
        console.error("Error saving edited image on mobile:", error);
      })
    } else if (index !== -1 && filesRef.current[index]) {
      const next = [...filesRef.current];
      next[index] = {
        previewUrl: URL.createObjectURL(editedImage),
        file: editedImage,
      };
      updateFiles(next);
    }
  }, [updateFiles, pictureSuccessHandler]);

  const handleImageDelete = useCallback((index: number = -1) => {
    if(index === -1) return;

    removeFile(index);
  }, [removeFile])

  const selectFile = useCallback((captureOption: number) => {
    if (!window.navigator?.camera) {
      console.error('Camera not available');
      return;
    }

    const cameraOptions = {
      quality: 100,
      destinationType: window.Camera.DestinationType.FILE_URI,
      correctOrientation: true,
      sourceType: captureOption === 1
        ? window.Camera.PictureSourceType.PHOTOLIBRARY
        : window.Camera.PictureSourceType.CAMERA,
    };

    const handleImage = (imageUri: string) => {
      if (!window.resolveLocalFileSystemURL) {
        return;
      }

      if (netWorkStatus || captureOption === 1) {
        pictureSuccessHandler(imageUri)
      } else {
        if (imageEdit) {
          const fileTransfer = new FileTransfer();
          fileTransfer.getBlobFromURL(imageUri).then(imagesBlob => {
            openEditor(
              imagesBlob,
              (editedImage) => handleImageEditSave(editedImage, -1, "CAMERA"),
              handleImageDelete,
              "CAMERA",
              false,
              () => pictureSuccessHandler(imageUri)
            );
          });
        } else {
          pictureSuccessHandler(imageUri);
        }
      }
    }

    window.navigator.camera.getPicture(
      handleImage,
      pictureErrorHandler,
      cameraOptions
    );
  }, [netWorkStatus, imageEdit, pictureSuccessHandler, pictureErrorHandler, openEditor, handleImageDelete, handleImageEditSave]);

  const hasNativeCamera = useCallback((): boolean => {
    return !!window['cordova'] && !!window.navigator?.camera && !!window['Camera'];
  }, []);

  const isAndroid = useCallback((): boolean => {
    return window['cordova']?.platformId === 'android';
  }, []);

  const openNativePicker = useCallback((mode: PickerMode): void => {
    const input = inputFileRef.current;
    if (!input) {
      return;
    }

    const acceptValue = resolvePickerAccept(accept ?? '', mode, window['cordova']?.platformId);
    if (acceptValue) {
      input.setAttribute('accept', acceptValue);
    } else {
      input.removeAttribute('accept');
    }

    if (mode === 'camera') {
      input.setAttribute('capture', 'environment');
    } else {
      input.removeAttribute('capture');
    }

    input.multiple = mode !== 'camera' && !!maxFiles && remainingFileSlots() > 1;

    input.click();
  }, [accept, maxFiles, remainingFileSlots]);

  const canOpenPicker = useCallback((): boolean => {
    if (fieldProps.loading || othersInputProps.disabled) {
      return false;
    }
    return hasFreeFileSlot();
  }, [fieldProps.loading, othersInputProps.disabled, hasFreeFileSlot]);

  const openCamera = useCallback((): void => {
    if (!canOpenPicker()) {
      return;
    }
    if (hasNativeCamera()) {
      selectFile(0);
      return;
    }
    openNativePicker('camera');
  }, [canOpenPicker, hasNativeCamera, selectFile, openNativePicker]);

  const openDocument = useCallback((): void => {
    if (!canOpenPicker()) {
      return;
    }
    openNativePicker('document');
  }, [canOpenPicker, openNativePicker]);

  const toggleDropDown: DropDownProps['onClose'] = useCallback((captureOption): void => {
    setIsOpenDropDown(open => !open);

    if (captureOption === 0) {
      openCamera();
    } else if (captureOption === 1) {
      openNativePicker('gallery');
    }
  }, [openCamera, openNativePicker]);

  const triggerFileInputClick = useCallback((e): void | false => {
    if (e) e.preventDefault();

    if (!onlyImage) {
      openDocument();
      return false;
    }

    if (isAndroid() && hasNativeCamera()) {
      if (!canOpenPicker()) {
        return false;
      }
      setIsOpenDropDown(true);
      return false;
    }

    openNativePicker('gallery');

    return false;
  }, [onlyImage, isAndroid, hasNativeCamera, canOpenPicker, openDocument, openNativePicker]);

  const triggerChange = useCallback((e): void => {
    const selected = Array.from(e.target.files || []) as File[];
    if (!selected.length) {
      return;
    }

    const currentFiles = filesRef.current;
    if (maxFiles && (currentFiles.length + selected.length > maxFiles)) {
      toast.error(t('exceedMaxFiles', { max: maxFiles }));
      return;
    }
    const incoming = selected;

    const needsRename = incoming.some(
      file => resolvePickedFileName(file) !== (file.name || '').trim(),
    );

    if (!needsRename) {
      updateFiles([
        ...currentFiles,
        ...incoming.map(file => ({
          previewUrl: URL.createObjectURL(file),
          file,
        })),
      ]);
      return;
    }

    const pending = incoming.map(file => normalizePickedFile(file));
    Promise.all(pending).then(
      (normalized) => {
        updateFiles([
          ...currentFiles,
          ...normalized.map(file => ({
            previewUrl: URL.createObjectURL(file),
            file,
          })),
        ]);
      },
      (error) => {
        console.error('Failed to process selected file(s)', error);
        updateFiles([
          ...currentFiles,
          ...incoming.map(file => ({
            previewUrl: URL.createObjectURL(file),
            file,
          })),
        ]);
      },
    );
  }, [maxFiles, t, updateFiles]);

  const editFile = useCallback((index) => {
    if(netWorkStatus) return;

    const current = filesRef.current[index]?.file;
    if(current) {
      openEditor(
        current,
        (editedImage) => handleImageEditSave(editedImage, index),
        () => handleImageDelete(index)
      )
    }
  }, [handleImageDelete, handleImageEditSave, netWorkStatus, openEditor]);

  useEffect(() => {
    if (defaultValue !== undefined) {
      const arr = Array.isArray(defaultValue) ? defaultValue : (defaultValue ? [defaultValue] : []);
      const currentValues = filesRef.current.map(f => f.file);
      if (!isEqual(currentValues, arr)) {
        const next = arr.map((file) => {
          return {
            previewUrl: typeof file === 'string' ? file : URL.createObjectURL(file),
            file: file,
          };
        });
        filesRef.current = next;
        setFiles(next);
      }
    }
  }, [defaultValue]);

  useImperativeHandle(ref, () => ({
    triggerFileInputClick,
    handleImageDelete,
    openCamera,
    openDocument,
  }));

  return (
    <Field {...fieldProps}>
      {/* `accept`, `multiple` and `capture` are set by openNativePicker right before the click,
          so they always describe the intent the user picked and React cannot overwrite them. */}
      <input
        {...othersInputProps}
        ref={inputFileRef}
        className={classNames(classes['input'], inputClassName)}
        type="file"
        disabled={fieldProps.loading || othersInputProps.disabled || (!!maxFiles && files.length >= maxFiles)}
        value=""
        onChange={triggerChange}
      />

      {(files.length > 0 && preview) && <Preview files={files} removeFile={removeFile} editFile={editFile} />}

      {showInput === true && (
        <div className={`${classes['file-input-container']} ${props.required ? classes['required'] : ''}`} onClick={triggerFileInputClick}>
          <Icon name={'camera_alt'} />
          <div className={classes['title']}>{placeholder}</div>
          {!!maxFiles && (
            <div className={classes['description']}>{t('maxFilesDescription', { max: maxFiles })}</div>
          )}
        </div>
      )}

      {isAndroid() && <DropDown open={isOpenDropDown} onClose={toggleDropDown} />}
    </Field>
  );
});

export default FileInput
